/**
 * BTA Button Tooltips
 * Kennel Club Boarding - kennelclubboarding.ca
 * Version: 2.0
 *
 * Injects a ⓘ icon inside BTA booking option buttons.
 * - Desktop: hover the ⓘ to show tooltip
 * - Mobile: tap ⓘ to show popup, tap elsewhere to dismiss
 * - Tapping ⓘ never triggers button selection
 * - Icon is gold when button is unselected, white when selected
 * - Calendar date buttons are excluded
 *
 * TO UPDATE TOOLTIP CONTENT: Edit the TOOLTIP_CONTENT object below only.
 * TO ADD A NEW BUTTON: Add a new entry matching the button's exact text.
 * TO DISABLE: Remove the <script> tag from theme.liquid.
 */

(function() {
  'use strict';

  // ============================================================================
  // DEBUG CONTROL — set to false before going to production
  // ============================================================================
  const DEBUG_MODE = false;

  const debug = {
    log: (...args) => { if (DEBUG_MODE) console.log('[BTA Tooltips]', ...args); },
    warn: (...args) => { if (DEBUG_MODE) console.warn('[BTA Tooltips]', ...args); }
  };

  // ============================================================================
  // TOOLTIP CONTENT
  // Keys must match button text exactly as it appears in the BTA widget.
  // Use \n for line breaks, * at start of line for bullet points.
  // Lines in ALL CAPS are rendered as section subheadings.
  // ============================================================================

  const TOOLTIP_CONTENT = {

    '2': {
      title: '2 Dogs',
      body: '2 dogs from the same household\n(same owner and address).'
    },

    '3': {
      title: '3 Dogs',
      body: '3 dogs from the same household\n(same owner and address).'
    },

    'Plays well with other dogs': {
      title: 'Plays well with other dogs',
      body: 'Select this option only if ALL of the following are true:\n* Plays well with all other dogs\n* Has never shown aggression towards other dogs or humans\n* Does not urinate or defecate indoors\n* Is not nervous meeting new people or dogs\n* Is well-behaved'
    },

    'Who do(es) not play well with others': {
      title: 'Does not play well with others',
      body: 'Select this option only if ALL of the following are true:\n* Has never shown aggression towards humans\n\nAND ANY ONE OR MORE of the following are true:\n* Is submissive\n* Is dominant\n* Does not respect other dogs\' social cues\n* Humps other dogs'
    },

    'Special care required': {
      title: 'Special care required',
      body: 'Select this option only if ALL of the following are true:\n* Plays well with other dogs\n* Has never shown aggression towards humans\n\nAND ANY of the following are true:\n* Urinates and/or defecates indoors\n* Is a senior dog with limited mobility\n* Requires daily medications (must be forced to take)\n* Requires injections (e.g. diabetic)\n* Requires ear medications\n* Requires wound care\n* Requires after-surgery care\n* Is not well-behaved'
    },

    'Bite Risk': {
      title: 'Bite Risk',
      body: 'Select if ONE OR MORE of the following are true:\n* Has shown aggression towards a person\n* Has bitten someone in the past'
    },

    'No Email Updates': {
      title: 'No Email Updates',
      body: 'You will not receive scheduled email updates during your dog\'s stay.\n\nPlease know that we will always contact you immediately if anything important happens, if your dog is unwell, or if there is any concern at all. Your dog\'s safety and well-being is always our top priority.'
    },

    'Email Updates (Every other day)': {
      title: 'Email Updates — Every Other Day',
      body: 'We\'ll send you an email update every other day with photos of your dog and how they\'re doing. Emails are usually sent at the end of the day (after the kennel closes) or early in the morning (before we open).\n\nRegardless of your email preference, we will always contact you immediately if anything important happens or if there is any concern about your dog.'
    },

    'Email Updates (Every day)': {
      title: 'Email Updates — Every Day',
      body: 'We\'ll send you a daily email update with photos of your dog and how they\'re doing. Emails are usually sent at the end of the day (after the kennel closes) or early in the morning (before we open).\n\nRegardless of your email preference, we will always contact you immediately if anything important happens or if there is any concern about your dog.'
    }

  };

  // ============================================================================
  // STYLES
  // ============================================================================

  const TOOLTIP_STYLES = `
    /* ⓘ icon injected inside buttons */
    .kcb-info-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 15px;
      height: 15px;
      border-radius: 50%;
      border: 1.5px solid #BD9B60;
      background: transparent;
      color: #BD9B60;
      font-size: 10px;
      font-weight: 700;
      font-style: normal;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      cursor: pointer;
      flex-shrink: 0;
      vertical-align: middle;
      line-height: 1;
      user-select: none;
      -webkit-user-select: none;
      margin-left: 8px;
      transition: border-color 0.15s ease, color 0.15s ease;
      position: relative;
      top: -1px;
    }

    .kcb-info-icon:active {
      opacity: 0.7;
    }

    /* Note: #kcb-tooltip-popup styles are injected into the main document,
       not here, since the popup renders outside the iframe. */
  `;

  // ============================================================================
  // CONTENT BUILDER — converts plain text format to HTML
  // ============================================================================

  function buildTooltipHTML(content) {
    const lines = content.body.split('\n');
    let html = '';
    let inList = false;

    for (const line of lines) {
      const trimmed = line.trim();

      if (trimmed === '') {
        if (inList) { html += '</ul>'; inList = false; }
        continue;
      }

      if (trimmed.startsWith('* ')) {
        if (!inList) { html += '<ul>'; inList = true; }
        html += '<li>' + trimmed.slice(2) + '</li>';
      } else {
        if (inList) { html += '</ul>'; inList = false; }
        // ALL CAPS lines = section subheading
        const isAllCaps = trimmed === trimmed.toUpperCase() && trimmed.length > 3 && /[A-Z]/.test(trimmed);
        if (isAllCaps) {
          html += '<p class="kcb-tooltip-section">' + trimmed + '</p>';
        } else {
          html += '<p>' + trimmed + '</p>';
        }
      }
    }

    if (inList) html += '</ul>';

    return '<div class="kcb-tooltip-title">' + content.title + '</div>'
         + '<div class="kcb-tooltip-body">' + html + '</div>';
  }

  // ============================================================================
  // SELECTED STATE DETECTION
  // Reads the button's computed background colour to determine if selected.
  // This is resilient to BTA's hashed styled-components class names changing.
  // ============================================================================

  function isButtonSelected(button, doc) {
    try {
      const iframeWindow = doc.defaultView || doc.parentWindow;
      if (!iframeWindow) return false;
      const bg = iframeWindow.getComputedStyle(button).backgroundColor;
      // BTA selected buttons get a gold/coloured background
      // Unselected buttons are dark (#222, #333) or transparent
      const rgb = bg.match(/\d+/g);
      if (!rgb || rgb.length < 3) return false;
      const r = parseInt(rgb[0]);
      const g = parseInt(rgb[1]);
      const b = parseInt(rgb[2]);
      // Detect any non-dark, non-transparent background
      // Dark buttons: r,g,b all < 80. Transparent: rgba(0,0,0,0)
      // Gold #BD9B60 = rgb(189,155,96) - clearly non-dark
      const isDark = r < 80 && g < 80 && b < 80;
      const isTransparent = r === 0 && g === 0 && b === 0;
      return !isDark && !isTransparent && (r + g + b) > 150;
    } catch (e) {
      return false;
    }
  }

  function updateIconColour(icon, button, doc) {
    const selected = isButtonSelected(button, doc);
    if (selected) {
      // White circle, white i
      icon.style.background = 'transparent';
      icon.style.border = '1.5px solid white';
      icon.style.color = 'white';
    } else {
      // Gold circle, gold i
      icon.style.background = 'transparent';
      icon.style.border = '1.5px solid #BD9B60';
      icon.style.color = '#BD9B60';
    }
  }

  // ============================================================================
  // POPUP — single shared element reused for all icons
  // ============================================================================

  // The popup lives in the MAIN document (outside the iframe) so it is never
  // clipped by the iframe boundary, regardless of where the button sits.
  function getOrCreatePopup() {
    const mainDoc = window.document;
    let popup = mainDoc.getElementById('kcb-tooltip-popup');
    if (!popup) {
      popup = mainDoc.createElement('div');
      popup.id = 'kcb-tooltip-popup';

      // Inject styles into main document too
      if (!mainDoc.getElementById('kcb-tooltip-popup-styles')) {
        const style = mainDoc.createElement('style');
        style.id = 'kcb-tooltip-popup-styles';
        style.textContent = `
          #kcb-tooltip-popup {
            position: fixed;
            z-index: 999999;
            background: #222222;
            color: #EAEAEA;
            border: 1px solid #BD9B60;
            border-radius: 8px;
            padding: 12px 16px;
            font-size: 13px;
            line-height: 1.6;
            max-width: 280px;
            min-width: 200px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            box-shadow: 0 4px 16px rgba(0,0,0,0.5);
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.15s ease;
          }
          #kcb-tooltip-popup.visible {
            opacity: 1;
            pointer-events: auto;
          }
          .kcb-tooltip-title {
            font-weight: 700;
            color: #BD9B60;
            margin-bottom: 8px;
            font-size: 13px;
            border-bottom: 1px solid #333;
            padding-bottom: 6px;
          }
          .kcb-tooltip-body ul {
            margin: 4px 0 0 0;
            padding-left: 16px;
          }
          .kcb-tooltip-body li {
            margin-bottom: 2px;
          }
          .kcb-tooltip-body p {
            margin: 4px 0 0 0;
          }
          .kcb-tooltip-section {
            margin-top: 8px;
            font-style: italic;
            color: #BD9B60;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.03em;
          }
          @media (max-width: 480px) {
            #kcb-tooltip-popup {
              max-width: 240px;
              font-size: 12px;
            }
          }
        `;
        mainDoc.head.appendChild(style);
      }

      mainDoc.body.appendChild(popup);
    }
    return popup;
  }

  function positionPopup(popup, icon) {
    // icon.getBoundingClientRect() gives coords relative to the iframe viewport.
    // We need coords relative to the MAIN document viewport.
    // Get the iframe element and its offset in the main document.
    const iframes = window.document.querySelectorAll('iframe');
    let iframeRect = { top: 0, left: 0 };
    for (const iframe of iframes) {
      try {
        if (iframe.contentDocument && iframe.contentDocument === icon.ownerDocument) {
          iframeRect = iframe.getBoundingClientRect();
          break;
        }
      } catch (e) { /* cross-origin */ }
    }

    const iconRect = icon.getBoundingClientRect();
    // Absolute position in main document viewport
    const absTop = iframeRect.top + iconRect.top;
    const absBottom = iframeRect.top + iconRect.bottom;
    const absLeft = iframeRect.left + iconRect.left;
    const absRight = iframeRect.left + iconRect.right;

    const mainViewportWidth = window.document.documentElement.clientWidth;
    const mainViewportHeight = window.document.documentElement.clientHeight;
    const popupWidth = 280;
    const estimatedHeight = 240;

    // Horizontal: centre on icon, clamp to main viewport edges
    let left = absLeft + iconRect.width / 2 - popupWidth / 2;
    if (left < 8) left = 8;
    if (left + popupWidth > mainViewportWidth - 8) left = mainViewportWidth - popupWidth - 8;

    // Vertical: prefer above, flip below if not enough room
    const spaceAbove = absTop;
    const spaceBelow = mainViewportHeight - absBottom;

    let top, transform;
    if (spaceAbove >= estimatedHeight || spaceAbove >= spaceBelow) {
      top = absTop;
      transform = 'translateY(calc(-100% - 8px))';
    } else {
      top = absBottom + 8;
      transform = 'none';
    }

    popup.style.left = left + 'px';
    popup.style.top = top + 'px';
    popup.style.transform = transform;
  }

  // ============================================================================
  // BUTTON MATCHING
  // Only matches option buttons, never calendar date buttons (rdp-day)
  // For number buttons (2, 3), only matches when siblings also contain 1/2/3
  // ============================================================================

  function isNumberButton(button) {
    const text = getButtonText(button);
    if (!['2', '3'].includes(text)) return false;
    // Must be in a group with a '1' button sibling to confirm it's the dog count section
    const parent = button.parentElement;
    if (!parent) return false;
    const siblings = parent.querySelectorAll('button');
    // Use getButtonText for siblings too so already-injected icons don't break matching
    const texts = Array.from(siblings).map(b => getButtonText(b));
    return texts.includes('1') && texts.includes('2') && texts.includes('3');
  }

  // Read only the button's direct text nodes, ignoring child element text.
  // This prevents the injected ⓘ icon's "i" from polluting the match
  // (e.g. button 2 becomes "2i" after injection if we use textContent).
  function getButtonText(button) {
    let text = '';
    button.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      }
    });
    return text.trim();
  }

  function shouldHaveTooltip(button) {
    // Never add to calendar date buttons
    if (button.classList.contains('rdp-day') ||
        button.classList.contains('rdp-button') ||
        button.classList.contains('rdp-nav_button')) return false;

    const text = getButtonText(button);

    // For number buttons, only match 2 and 3 in the dog count group
    if (/^\d+$/.test(text)) return isNumberButton(button);

    // For all other buttons, check TOOLTIP_CONTENT
    return !!TOOLTIP_CONTENT[text];
  }

  // ============================================================================
  // INJECTION
  // ============================================================================

  function injectButtonTooltips(doc) {
    if (!doc) return;
    if (doc.getElementById('kcb-button-tooltip-styles')) {
      debug.log('Already injected — skipping');
      return;
    }

    debug.log('Injecting button tooltips...');

    // Inject styles
    const styleEl = doc.createElement('style');
    styleEl.id = 'kcb-button-tooltip-styles';
    styleEl.textContent = TOOLTIP_STYLES;
    doc.head.appendChild(styleEl);

    const popup = getOrCreatePopup();
    const isMobile = 'ontouchstart' in window;
    let activeIcon = null;
    let dismissTimeout = null;

    function showPopup(icon, content) {
      clearTimeout(dismissTimeout);
      popup.innerHTML = buildTooltipHTML(content) + `
        <div style="display:flex;justify-content:space-between;align-items:center;margin-top:10px;padding-top:8px;border-top:1px solid #333;">
          <button id="kcb-tooltip-close" style="
            background:transparent;border:1px solid #555;border-radius:4px;
            color:#aaa;font-size:11px;padding:2px 8px;cursor:pointer;
            font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
            transition:border-color 0.15s,color 0.15s;
          ">Close</button>
          <svg width="20" height="20" viewBox="0 0 20 20" style="transform:rotate(-90deg);">
            <circle cx="10" cy="10" r="8" fill="none" stroke="#333" stroke-width="2"/>
            <circle cx="10" cy="10" r="8" fill="none" stroke="#BD9B60" stroke-width="2"
              stroke-dasharray="50.3" stroke-dashoffset="0"
              style="transition:stroke-dashoffset linear;stroke-linecap:round;"
              id="kcb-countdown-ring"/>
          </svg>
        </div>`;
      positionPopup(popup, icon);
      popup.classList.add('visible');
      activeIcon = icon;

      // Wire up close button
      const closeBtn = window.document.getElementById('kcb-tooltip-close');
      if (closeBtn) {
        closeBtn.style.pointerEvents = 'auto';
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          clearTimeout(dismissTimeout);
          popup.classList.remove('visible');
          activeIcon = null;
        });
        closeBtn.addEventListener('mouseenter', () => {
          closeBtn.style.borderColor = '#BD9B60';
          closeBtn.style.color = '#BD9B60';
        });
        closeBtn.addEventListener('mouseleave', () => {
          closeBtn.style.borderColor = '#555';
          closeBtn.style.color = '#aaa';
        });
      }

      // Animate the ring draining over the dismiss timeout duration
      requestAnimationFrame(() => {
        const ring = window.document.getElementById('kcb-countdown-ring');
        if (ring) {
          ring.style.transitionDuration = '20000ms';
          ring.style.strokeDashoffset = '50.3';
        }
      });

      debug.log('Showing: ' + content.title);
    }

    function hidePopup() {
      popup.classList.remove('visible');
      activeIcon = null;
    }

    // Watch for button class changes to update icon colour
    function watchButtonState(icon, button) {
      updateIconColour(icon, button, doc);
      // Watch for class AND style changes - BTA may change either when selecting
      const observer = new MutationObserver(() => {
        updateIconColour(icon, button, doc);
      });
      observer.observe(button, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        subtree: false
      });
      // Also watch the button's parent in case BTA re-renders siblings
      if (button.parentElement) {
        const parentObserver = new MutationObserver(() => {
          updateIconColour(icon, button, doc);
        });
        parentObserver.observe(button.parentElement, {
          attributes: true,
          subtree: true,
          attributeFilter: ['class', 'style']
        });
      }
    }

    const buttons = doc.querySelectorAll('button');
    let injected = 0;

    buttons.forEach(button => {
      if (!shouldHaveTooltip(button)) return;

      const text = getButtonText(button);
      const content = TOOLTIP_CONTENT[text];
      if (!content) return;

      // Make the button a flex container so text stays left and icon sits right
      const existingDisplay = button.style.display;
      if (!existingDisplay || existingDisplay === 'inline' || existingDisplay === 'block') {
        button.style.display = 'inline-flex';
        button.style.alignItems = 'center';
        button.style.justifyContent = 'space-between';
      }

      // Create the ⓘ icon
      const icon = doc.createElement('span');
      icon.className = 'kcb-info-icon';
      icon.textContent = 'i';
      icon.setAttribute('aria-label', 'More information');
      icon.setAttribute('role', 'button');

      // Watch button state for colour updates
      watchButtonState(icon, button);

      if (isMobile) {
        icon.addEventListener('touchend', (e) => {
          e.stopPropagation();
          e.preventDefault(); // critical: prevents button selection
          if (activeIcon === icon && popup.classList.contains('visible')) {
            hidePopup();
          } else {
            showPopup(icon, content);
            dismissTimeout = setTimeout(hidePopup, 20000);
          }
        });
      } else {
        icon.addEventListener('mouseenter', () => showPopup(icon, content));
        icon.addEventListener('mouseleave', () => hidePopup());
      }

      // On click, update ALL injected icons repeatedly over 600ms
      // to catch BTA's async React re-render updating button backgrounds
      button.addEventListener('click', () => {
        [150, 300, 450, 600].forEach(delay => {
          setTimeout(() => {
            doc.querySelectorAll('.kcb-info-icon').forEach(otherIcon => {
              if (otherIcon._kcbButton) {
                updateIconColour(otherIcon, otherIcon._kcbButton, doc);
              }
            });
          }, delay);
        });
      });
      icon._kcbButton = button;
      button.appendChild(icon);
      injected++;
      debug.log('Injected ⓘ into: "' + text + '"');
    });

    // Dismiss on tap outside (mobile)
    if (isMobile) {
      doc.addEventListener('touchend', (e) => {
        if (activeIcon && !activeIcon.contains(e.target)) {
          hidePopup();
          clearTimeout(dismissTimeout);
        }
      });
    }

    debug.log('Done. Icons injected: ' + injected);
  }

  // ============================================================================
  // IFRAME DETECTION
  // ============================================================================

  function getFreshIframeDoc() {
    const selectors = [
      'iframe[title="widget_xcomponent"]',
      '#bta-widget-container iframe',
      'iframe[id*="bta-product-widget"]',
      'iframe[src*="book.app"]',
      'iframe[src*="bookthatapp"]'
    ];
    for (const selector of selectors) {
      const candidates = document.querySelectorAll(selector);
      for (const candidate of candidates) {
        if (candidate.offsetWidth > 0 && candidate.offsetHeight > 0) {
          try {
            const doc = candidate.contentDocument || candidate.contentWindow?.document;
            if (doc && doc.body) return doc;
          } catch (e) { /* cross-origin */ }
        }
      }
    }
    return null;
  }

  // ============================================================================
  // WATCHER — re-injects if BTA rebuilds its widget
  // ============================================================================

  let watcherInterval = null;
  let lastDoc = null;

  function startWatcher() {
    if (watcherInterval) return;
    watcherInterval = setInterval(() => {
      const doc = getFreshIframeDoc();
      if (!doc) return;
      if (doc !== lastDoc) {
        debug.log('Widget rebuilt — re-injecting');
        lastDoc = doc;
        injectButtonTooltips(doc);
      }
    }, 2000);
    debug.log('Watcher started');
  }

  // ============================================================================
  // INIT
  // ============================================================================

  function init() {
    debug.log('BTA Button Tooltips v2.0 — init');

    let attempts = 0;
    const maxAttempts = 30;

    const tryInject = () => {
      attempts++;
      const doc = getFreshIframeDoc();

      if (!doc) {
        if (attempts < maxAttempts) setTimeout(tryInject, 500);
        else debug.warn('Giving up — iframe not found');
        return;
      }

      // Wait for widget to be fully rendered
      if (!doc.querySelector('button.pure-button.fluid')) {
        if (attempts < maxAttempts) setTimeout(tryInject, 500);
        else debug.warn('Giving up — widget not ready');
        return;
      }

      debug.log('Widget ready on attempt ' + attempts);
      lastDoc = doc;
      injectButtonTooltips(doc);
      startWatcher();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(tryInject, 500));
    } else {
      setTimeout(tryInject, 500);
    }
  }

  // Run on boarding and daycare product pages
  if (window.location.pathname.includes('/products/dog-boarding') || window.location.pathname.includes('/products/dog-daycare')) {
    init();
  }

})();